import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MentionInput from '../../components/MentionInput/MentionInput';
import MentionText from '../../components/MentionInput/MentionText';

interface Comment {
    id: number;
    comment: string;
    user: string;
    userId: number;
    createdAt: string;
}

interface WarehouseCommentsProps {
    warehouseRecordId: number;
    warehouseActions: any;
}

const AVATAR_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];

const getAvatarColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name: string) => {
    return name.split(' ').map(w => w.charAt(0)).join('').substring(0, 2).toUpperCase() || '??';
};

const WarehouseComments: React.FC<WarehouseCommentsProps> = ({ warehouseRecordId, warehouseActions }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [comments, setComments] = useState<Comment[]>([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    const fetchComments = () => {
        setLoading(true);
        warehouseActions.getWarehouseRecordComments(warehouseRecordId).then((res: any) => {
            const data = res?.data?.data || res?.data || [];
            if (Array.isArray(data)) setComments(data);
            setLoading(false);
            setTimeout(() => {
                if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
            }, 50);
        });
    };

    useEffect(() => {
        if (warehouseRecordId) fetchComments();
    }, [warehouseRecordId]);

    const handleSend = () => {
        if (!newComment.trim() || sending) return;
        setSending(true);
        warehouseActions.addWarehouseRecordComment(warehouseRecordId, newComment.trim()).then((res: any) => {
            const comment = res?.data?.data || res?.data;
            if (comment?.id) {
                setComments(prev => [...prev, comment]);
                setNewComment('');
                setTimeout(() => {
                    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
                }, 50);
            }
            setSending(false);
        });
    };

    const handleDelete = (commentId: number) => {
        warehouseActions.deleteWarehouseRecordComment(commentId).then((res: any) => {
            const success = res?.data?.data ?? res?.data;
            if (success) setComments(prev => prev.filter(c => c.id !== commentId));
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="wsc-comments">
            <div className="wsc-comments-list" ref={listRef}>
                {loading && comments.length === 0 ? (
                    <div className="wsc-comments-empty">
                        <span><FormattedMessage id="production.board.loading" defaultMessage="Duke ngarkuar..." /></span>
                    </div>
                ) : comments.length === 0 ? (
                    <div className="wsc-comments-empty">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                        <span><FormattedMessage id="warehouse.comments.empty" defaultMessage="Asnjë koment ende" /></span>
                    </div>
                ) : (
                    comments.map(c => {
                        const userName = c.user || '??';
                        const initials = getInitials(userName);
                        const avatarColor = getAvatarColor(userName);
                        return (
                            <div key={c.id} className="wsc-comment-item">
                                <div className="wsc-comment-avatar" style={{ background: avatarColor }}>{initials}</div>
                                <div className="wsc-comment-content">
                                    <div className="wsc-comment-meta">
                                        <span className="wsc-comment-user">{userName}</span>
                                        <span className="wsc-comment-dot">&middot;</span>
                                        <span className="wsc-comment-time">{c.createdAt}</span>
                                    </div>
                                    <p className="wsc-comment-text"><MentionText text={c.comment} /></p>
                                </div>
                                <button className="wsc-comment-delete" onClick={() => handleDelete(c.id)} type="button"
                                    title={fm('common.action.delete', 'Fshi')}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
            <div className="wsc-comments-input">
                <MentionInput
                    multiline
                    value={newComment}
                    onChange={(next) => setNewComment(next)}
                    onKeyDown={handleKeyDown}
                    placeholder={fm('warehouse.comments.placeholder', 'Shkruaj komentin...')}
                    rows={1}
                />
                <button className="wsc-comments-send" onClick={handleSend} disabled={!newComment.trim() || sending} type="button"
                    title={fm('warehouse.comments.send', 'Dërgo')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                </button>
            </div>
        </div>
    );
};

export default WarehouseComments;
